import { promptInput, type SettingsRow } from '@bn-games/ui-shell';
import type {
  Difficulty,
  Language,
  Settings,
  ThemeName,
  Variant,
} from '../settings/types.js';
import type { PlayerCount } from '../game/variants/types.js';
import { getRuleSet } from '../game/variants/index.js';
import { soloSibling } from './types.js';
import { mugginsRuleRows, scoringRuleRows, type RuleRowsInput } from './ruleRows.js';
import { t } from '../i18n/I18n.js';

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard'];
const THEME_NAMES: readonly ThemeName[] = ['mahogany', 'persian', 'deco', 'monte'];
const LANGUAGES: readonly Language[] = ['en', 'ru'];
const VARIANTS: readonly Variant[] = [
  'goat',
  'goat-2v2',
  'telefon',
  'telefon-2v2',
  'all-fives',
  'all-fives-2v2',
];
const PLAYER_COUNTS: readonly PlayerCount[] = [2, 3, 4];

/** Minimal contract the row builder needs — both MenuContext and the
 *  pause-menu adapter satisfy this. */
export interface SettingsRowCtx {
  getSettings(): Settings;
  updateSettings(partial: Partial<Settings>): void;
}

export interface BuildSettingsRowsOptions {
  readonly ctx: SettingsRowCtx;
  /** Pause-menu wraps game-config changes with confirm + restart. */
  readonly onGameConfigChange?: (apply: () => void) => void;
  /** Disable game-config rows entirely (online matches). */
  readonly disableGameConfig?: boolean;
}

export function buildSettingsRows(opts: BuildSettingsRowsOptions): readonly SettingsRow[] {
  const { ctx, onGameConfigChange, disableGameConfig = false } = opts;
  const s = ctx.getSettings();

  const applyGameConfig = (partial: Partial<Settings>): void => {
    const apply = (): void => ctx.updateSettings(partial);
    if (onGameConfigChange) onGameConfigChange(apply);
    else apply();
  };

  // Variant / playerCount have a cross-constraint: the 2v2 variants imply
  // playerCount=4. Mirror the rules that lived in the old SettingsScreen so
  // a change here can't produce an inconsistent pair.
  const onVariantChange = (next: Variant): void => {
    if (getRuleSet(next).partnership) {
      applyGameConfig({ variant: next, playerCount: 4 });
    } else {
      applyGameConfig({ variant: next });
    }
  };
  const onPlayerCountChange = (next: PlayerCount): void => {
    if (next !== 4 && getRuleSet(s.variant).partnership) {
      applyGameConfig({ playerCount: next, variant: soloSibling(s.variant) });
    } else {
      applyGameConfig({ playerCount: next });
    }
  };

  const ruleRowsInput: RuleRowsInput = {
    settings: s,
    applyGameConfig,
    updateSettings: (partial) => ctx.updateSettings(partial),
    disableGameConfig,
  };

  return [
    {
      kind: 'text',
      key: 'playerName',
      label: t('settings.rows.playerName'),
      value: s.playerName,
      onClick: () => {
        void promptInput({
          title: t('settings.namePrompt'),
          defaultValue: s.playerName,
        }).then((v) => {
          if (v === null) return;
          const trimmed = v.slice(0, 16);
          if (trimmed) ctx.updateSettings({ playerName: trimmed });
        });
      },
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'sound',
      label: t('settings.rows.sound'),
      value: s.sound,
      onClick: () => ctx.updateSettings({ sound: !s.sound }),
      tab: 'audio',
    },
    {
      kind: 'selector',
      key: 'difficulty',
      label: t('settings.rows.difficulty'),
      value: s.difficulty,
      options: DIFFICULTIES,
      format: (v) => t(`settings.difficulties.${v as Difficulty}`),
      onChange: (next) => applyGameConfig({ difficulty: next as Difficulty }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'variant',
      label: t('settings.rows.variant'),
      value: s.variant,
      options: VARIANTS,
      format: (v) => t(`settings.variants.${v as Variant}`),
      onChange: (next) => onVariantChange(next as Variant),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'playerCount',
      label: t('settings.rows.playerCount'),
      value: String(s.playerCount),
      options: PLAYER_COUNTS.map((v) => String(v)),
      format: (v) => v,
      onChange: (next) => onPlayerCountChange(Number(next) as PlayerCount),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    ...scoringRuleRows(ruleRowsInput),
    {
      kind: 'selector',
      key: 'theme',
      label: t('settings.rows.theme'),
      value: s.theme,
      options: THEME_NAMES,
      format: (v) => t(`settings.themes.${v as ThemeName}`),
      onChange: (next) => ctx.updateSettings({ theme: next as ThemeName }),
      tab: 'interface',
    },
    {
      kind: 'selector',
      key: 'language',
      label: t('settings.rows.language'),
      value: s.language,
      options: LANGUAGES,
      format: (v) => t(`settings.languages.${v as Language}`),
      onChange: (next) => ctx.updateSettings({ language: next as Language }),
      tab: 'interface',
    },
    ...mugginsRuleRows(ruleRowsInput),
    {
      kind: 'toggle',
      key: 'hotSeatHide',
      label: t('settings.rows.hotSeatHide'),
      value: s.hotSeatHide,
      onClick: () => ctx.updateSettings({ hotSeatHide: !s.hotSeatHide }),
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'afkAssistEnabled',
      label: t('settings.rows.afkAssistEnabled'),
      value: s.afkAssistEnabled,
      onClick: () => ctx.updateSettings({ afkAssistEnabled: !s.afkAssistEnabled }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'afkRemindersEnabled',
      label: t('settings.rows.afkRemindersEnabled'),
      value: s.afkRemindersEnabled,
      onClick: () => ctx.updateSettings({ afkRemindersEnabled: !s.afkRemindersEnabled }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'turnStartSound',
      label: t('settings.rows.turnStartSound'),
      value: s.turnStartSound,
      onClick: () => ctx.updateSettings({ turnStartSound: !s.turnStartSound }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'showAfkPingButton',
      label: t('settings.rows.showAfkPingButton'),
      value: s.showAfkPingButton,
      onClick: () => ctx.updateSettings({ showAfkPingButton: !s.showAfkPingButton }),
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'verifiableDiceEnabled',
      label: t('settings.rows.verifiableDiceEnabled'),
      value: s.verifiableDiceEnabled,
      onClick: () => ctx.updateSettings({ verifiableDiceEnabled: !s.verifiableDiceEnabled }),
      // Mid-match: a flip would mis-stream the protocol for the running room.
      // The toggle only takes effect at the next CREATE_ROOM.
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'showBoneyardCount',
      label: t('settings.rows.showBoneyardCount'),
      value: s.showBoneyardCount,
      onClick: () => ctx.updateSettings({ showBoneyardCount: !s.showBoneyardCount }),
      tab: 'interface',
    },
  ];
}
